import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL;

const statusColor: Record<string, string> = {
  calculated: 'bg-blue-100 text-blue-800',
  partial_paid: 'bg-orange-100 text-orange-800',
  paid: 'bg-green-100 text-green-800',
  voided: 'bg-gray-200 text-slate-700 dark:text-slate-300',
};

export default function Earnings() {
  const [periods, setPeriods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/affiliate-portal/earnings`, { credentials: 'include' })
      .then((r) => r.json() as Promise<{ success: boolean; data: any[] }>)
      .then((j) => { if (j.success) setPeriods(j.data); setLoading(false); });
  }, []);

  if (loading) return <div className="text-center text-slate-500 dark:text-slate-400 py-8">Loading...</div>;

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Earnings History</h2>
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
        Your monthly commission periods. Click a row to expand and see the per-referral breakdown.
      </p>

      {periods.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 text-center text-slate-500 dark:text-slate-400">
          No periods yet. Once you have vested referrals, monthly commission periods will appear here.
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-slate-800">
              <tr>
                <th className="text-left p-3">Period</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Gross Revenue</th>
                <th className="text-right p-3">Rate</th>
                <th className="text-right p-3">Earned</th>
                <th className="text-right p-3">Paid</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => (
                <>
                  <tr key={p.id} onClick={() => setExpanded(expanded === p.id ? null : p.id)} className="border-b dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer">
                    <td className="p-3 font-medium">{p.period_start}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${statusColor[p.status] || 'bg-gray-100'}`}>{p.status}</span></td>
                    <td className="p-3 text-right font-mono">${Number(p.gross_referral_revenue || 0).toFixed(2)}</td>
                    <td className="p-3 text-right">{p.tier_rate_applied}%</td>
                    <td className="p-3 text-right font-mono font-bold">${Number(p.total_due || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-mono">${Number(p.total_paid || 0).toFixed(2)}</td>
                  </tr>
                  {expanded === p.id && (
                    <tr className="bg-gray-50 dark:bg-slate-800">
                      <td colSpan={6} className="p-4">
                        {p.line_items?.length > 0 ? (
                          <div>
                            <div className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Line items</div>
                            <table className="w-full text-xs">
                              <thead><tr><th className="text-left p-1">Type</th><th className="text-left p-1">From</th><th className="text-right p-1">Revenue</th><th className="text-right p-1">Rate</th><th className="text-right p-1">Commission</th></tr></thead>
                              <tbody>
                                {p.line_items.map((li: any) => (
                                  <tr key={li.id} className="border-b border-gray-200">
                                    <td className="p-1">{li.line_type}</td>
                                    <td className="p-1 font-mono">{li.referred_location_id?.slice(0, 8)}...</td>
                                    <td className="p-1 text-right font-mono">${Number(li.gross_revenue).toFixed(2)}</td>
                                    <td className="p-1 text-right">{li.rate_applied}%</td>
                                    <td className="p-1 text-right font-mono">${Number(li.commission_amount).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500 dark:text-slate-400">No line items for this period.</div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
